/**
 * `<caos-notice-banner>` — a persistent page- or section-level notice that a toast
 * would be wrong for, because it needs a decision or must not disappear.
 * (Catalog: Status & feedback › NoticeBanner — a persistent page- or section-level notice that a toast would be wrong for, because it needs a decision or must not disappear.)
 *
 * Intents: info (default) · success · warning · danger (with `error` accepted as an
 * alias). Each reads the SEMANTIC `--caos-color-{info,success,warning,danger}` tokens:
 * the `-bg` for the fill, the `-text` / `-text-muted` on-tint INK for the title / body
 * (NOT the neutral text token, which flips to near-white in dark and would be invisible
 * on the pale fill). These are DECOUPLED from the brand accent (design system), so a
 * danger banner stays red when `data-brand` repaints the accent. `dismissable` shows
 * a close control that fires a cancelable `caos-notice-dismiss` event.
 *
 * Content: a `title` attribute (optional) above the default slot (the body). A named
 * `actions` slot holds any decision controls on the right. Styling seams:
 * `::part(banner)`, `::part(icon)`, `::part(content)`, `::part(title)`, `::part(dismiss)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { intentIcon, makeIcon } from './icons.js';

export type CaosNoticeIntent = 'info' | 'success' | 'warning' | 'danger';

const sheet = makeSheet(`
  :host { display: block; }

  .banner {
    display: flex;
    align-items: flex-start;
    gap: var(--caos-space-3);
    padding: var(--caos-space-3) var(--caos-space-4);
    border-radius: var(--caos-radius);
    border: 1px solid transparent;
    font-family: var(--caos-font);
    /* default = info; semantic tokens, NOT brand accent. Text uses the on-tint INK
     * (not the theme-flipping neutral text token) so it stays legible on the pale
     * -bg fill in BOTH light and dark. */
    background: var(--caos-color-info-bg);
    border-color: color-mix(in srgb, var(--caos-color-info) 35%, transparent);
    color: var(--caos-color-info-text-muted);
  }

  :host([intent="success"]) .banner {
    background: var(--caos-color-success-bg);
    border-color: color-mix(in srgb, var(--caos-color-success) 35%, transparent);
    color: var(--caos-color-success-text-muted);
  }
  :host([intent="warning"]) .banner {
    background: var(--caos-color-warning-bg);
    border-color: color-mix(in srgb, var(--caos-color-warning) 35%, transparent);
    color: var(--caos-color-warning-text-muted);
  }
  :host([intent="danger"]) .banner,
  :host([intent="error"]) .banner {
    background: var(--caos-color-danger-bg);
    border-color: color-mix(in srgb, var(--caos-color-danger) 35%, transparent);
    color: var(--caos-color-danger-text-muted);
  }

  .icon { flex: none; width: 18px; height: 18px; margin-top: 1px; color: var(--caos-color-info); }
  :host([intent="success"]) .icon { color: var(--caos-color-success); }
  :host([intent="warning"]) .icon { color: var(--caos-color-warning); }
  :host([intent="danger"]) .icon,
  :host([intent="error"]) .icon { color: var(--caos-color-danger); }
  .icon svg { display: block; width: 100%; height: 100%; }

  .content { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .title {
    font-size: var(--caos-text-row);
    font-weight: 600; /* banner-title emphasis — off-scale weight, stays literal */
    line-height: 1.4;
    /* darker on-tint ink for the heading; the body inherits the banner's muted ink */
    color: var(--caos-color-info-text);
  }
  :host([intent="success"]) .title { color: var(--caos-color-success-text); }
  :host([intent="warning"]) .title { color: var(--caos-color-warning-text); }
  :host([intent="danger"]) .title,
  :host([intent="error"]) .title { color: var(--caos-color-danger-text); }
  .title:empty { display: none; }
  .body { font-size: 13px; line-height: 1.5; color: inherit; }

  .actions { flex: none; display: flex; align-items: center; gap: var(--caos-space-2); }
  ::slotted([slot="actions"]) { margin-left: var(--caos-space-1); }

  .dismiss {
    flex: none;
    display: none;
    appearance: none;
    border: none;
    background: transparent;
    /* inherit the banner's on-tint ink so the close control is legible on the fill
     * in both themes (the neutral text token would flip to near-white in dark). */
    color: inherit;
    width: 22px;
    height: 22px;
    padding: 3px;
    margin: -2px -4px -2px 0;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
  }
  .dismiss svg { display: block; width: 100%; height: 100%; }
  :host([dismissable]) .dismiss { display: inline-flex; }
  .dismiss:hover { background: color-mix(in srgb, currentColor 12%, transparent); }
  .dismiss:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
`);

export class CaosNoticeBanner extends CaosElement {
  static get observedAttributes(): string[] {
    return ['intent', 'title', 'dismissable'];
  }

  private banner: HTMLDivElement;
  private iconEl: HTMLSpanElement;
  private titleEl: HTMLDivElement;

  constructor() {
    super(sheet);

    this.banner = document.createElement('div');
    this.banner.className = 'banner';
    this.banner.setAttribute('part', 'banner');
    this.banner.setAttribute('role', 'status');

    this.iconEl = document.createElement('span');
    this.iconEl.className = 'icon';
    this.iconEl.setAttribute('part', 'icon');
    this.iconEl.setAttribute('aria-hidden', 'true');

    const content = document.createElement('div');
    content.className = 'content';
    content.setAttribute('part', 'content');
    this.titleEl = document.createElement('div');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    const body = document.createElement('div');
    body.className = 'body';
    body.append(document.createElement('slot'));
    content.append(this.titleEl, body);

    const actions = document.createElement('div');
    actions.className = 'actions';
    const actionsSlot = document.createElement('slot');
    actionsSlot.name = 'actions';
    actions.append(actionsSlot);

    const dismiss = document.createElement('button');
    dismiss.className = 'dismiss';
    dismiss.type = 'button';
    dismiss.setAttribute('part', 'dismiss');
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.append(makeIcon('close'));
    dismiss.addEventListener('click', () => {
      const ev = new CustomEvent('caos-notice-dismiss', { bubbles: true, composed: true, cancelable: true });
      if (this.dispatchEvent(ev)) this.remove();
    });

    this.banner.append(this.iconEl, content, actions, dismiss);
    this.root.append(this.banner);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    const intent = this.getAttribute('intent') ?? 'info';
    this.iconEl.replaceChildren(intentIcon(intent));
    // danger reads as an alert to assistive tech; the rest are polite status.
    this.banner.setAttribute('role', intent === 'danger' || intent === 'error' ? 'alert' : 'status');
    this.titleEl.textContent = this.getAttribute('title') ?? '';
  }
}

define('caos-notice-banner', CaosNoticeBanner);

declare global {
  interface HTMLElementTagNameMap {
    'caos-notice-banner': CaosNoticeBanner;
  }
}
